import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { TourTooltip } from './TourTooltip';

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const OVERLAY_CLASS = 'tour-overlay-panel';

// Helper to remove all overlay panels
function removeOverlayPanels() {
  document.querySelectorAll(`.${OVERLAY_CLASS}`).forEach(el => el.remove());
}

export function TourOverlay() {
  const { isActive, currentStep, currentTour } = useOnboarding();
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Create and manage overlay panels directly on body
  useEffect(() => {
    if (!isActive || !isVisible) {
      removeOverlayPanels();
      return;
    }

    // Remove any existing panels first
    removeOverlayPanels();

    const padding = 8;
    const spotlightRect = targetRect
      ? {
          top: targetRect.top - padding,
          left: targetRect.left - padding,
          width: targetRect.width + padding * 2,
          height: targetRect.height + padding * 2,
        }
      : null;

    if (spotlightRect) {
      // Create 4 panels for the overlay - append directly to body
      const panelConfigs = [
        // Top panel
        {
          top: '0',
          left: '0',
          right: '0',
          height: `${Math.max(0, spotlightRect.top)}px`,
          bottom: 'auto',
          width: 'auto',
        },
        // Bottom panel
        {
          top: `${spotlightRect.top + spotlightRect.height}px`,
          left: '0',
          right: '0',
          bottom: '0',
          height: 'auto',
          width: 'auto',
        },
        // Left panel
        {
          top: `${Math.max(0, spotlightRect.top)}px`,
          left: '0',
          width: `${Math.max(0, spotlightRect.left)}px`,
          height: `${spotlightRect.height}px`,
          right: 'auto',
          bottom: 'auto',
        },
        // Right panel
        {
          top: `${Math.max(0, spotlightRect.top)}px`,
          left: `${spotlightRect.left + spotlightRect.width}px`,
          right: '0',
          height: `${spotlightRect.height}px`,
          width: 'auto',
          bottom: 'auto',
        },
      ];

      panelConfigs.forEach((config) => {
        const panel = document.createElement('div');
        panel.className = OVERLAY_CLASS;
        panel.style.cssText = `
          position: fixed;
          background: black;
          opacity: 0.75;
          pointer-events: none;
          transition: all 0.3s ease;
          z-index: 9998;
          top: ${config.top};
          left: ${config.left};
          right: ${config.right};
          bottom: ${config.bottom};
          width: ${config.width};
          height: ${config.height};
        `;
        document.body.appendChild(panel);
      });

      // Create spotlight highlight ring
      const highlight = document.createElement('div');
      highlight.className = OVERLAY_CLASS;
      highlight.style.cssText = `
        position: fixed;
        top: ${spotlightRect.top}px;
        left: ${spotlightRect.left}px;
        width: ${spotlightRect.width}px;
        height: ${spotlightRect.height}px;
        border-radius: 12px;
        box-shadow: 0 0 0 4px #9B6BFF, 0 0 0 6px white;
        pointer-events: none;
        transition: all 0.3s ease;
        z-index: 9999;
      `;
      document.body.appendChild(highlight);
    } else {
      // Full screen overlay when no target
      const fullOverlay = document.createElement('div');
      fullOverlay.className = OVERLAY_CLASS;
      fullOverlay.style.cssText = `
        position: fixed;
        inset: 0;
        background: black;
        opacity: 0.75;
        pointer-events: none;
        z-index: 9998;
      `;
      document.body.appendChild(fullOverlay);
    }

    return () => {
      // Cleanup handled by removeOverlayPanels in next effect run
    };
  }, [isActive, isVisible, targetRect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      removeOverlayPanels();
    };
  }, []);

  // Find and track target element
  useEffect(() => {
    if (!isActive || !currentStep) {
      setTargetRect(null);
      setIsVisible(false);
      return;
    }

    const findTarget = () => {
      const target = document.querySelector(currentStep.target);
      if (target) {
        const rect = target.getBoundingClientRect();
        setTargetRect({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        });

        // Scroll target into view first
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });

        // Wait for scroll to complete before showing
        setTimeout(() => {
          const newRect = target.getBoundingClientRect();
          setTargetRect({
            top: newRect.top,
            left: newRect.left,
            width: newRect.width,
            height: newRect.height,
          });
          setIsVisible(true);
        }, 400);
      } else {
        setTargetRect(null);
        setIsVisible(true);
      }
    };

    const timer = setTimeout(findTarget, 100);

    const handleUpdate = () => {
      const target = document.querySelector(currentStep.target);
      if (target) {
        const rect = target.getBoundingClientRect();
        setTargetRect({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        });
      }
    };

    window.addEventListener('scroll', handleUpdate);
    window.addEventListener('resize', handleUpdate);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleUpdate);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isActive, currentStep]);

  if (!isActive || !isVisible) return null;

  const padding = 8;
  const spotlightRect = targetRect
    ? {
        top: targetRect.top - padding,
        left: targetRect.left - padding,
        width: targetRect.width + padding * 2,
        height: targetRect.height + padding * 2,
      }
    : null;

  // Only render the tooltip via React portal - overlay panels are on body directly
  return createPortal(
    <div className="fixed inset-0 z-[10000] pointer-events-none">
      {/* Tooltip */}
      {currentStep && currentTour && (
        <TourTooltip targetRect={spotlightRect} />
      )}
    </div>,
    document.body
  );
}
